import type { ApiErrorBody, DiskFile, FilesResponse, Retention, UploadTicket } from "../shared/contracts";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code = "UNKNOWN_ERROR",
    public readonly status = 0,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: init?.body
      ? { "Content-Type": "application/json", ...init.headers }
      : init?.headers,
  });
  if (!response.ok) {
    let body: ApiErrorBody | null = null;
    try {
      body = (await response.json()) as ApiErrorBody;
    } catch {
      // An infrastructure error may return HTML instead of the API error format.
    }
    throw new ApiError(body?.error.message ?? "请求失败，请稍后重试", body?.error.code, response.status);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export function getSession(): Promise<{ email: string }> {
  return request("/api/session");
}

export function login(password: string): Promise<{ email: string }> {
  return request("/api/auth/login", { method: "POST", body: JSON.stringify({ password }) });
}

export function logout(): Promise<void> {
  return request("/api/auth/logout", { method: "POST", body: "{}" });
}

export function getFiles(): Promise<FilesResponse> {
  return request("/api/files");
}

export function createUpload(file: File, retention: Retention): Promise<UploadTicket> {
  return request("/api/uploads", {
    method: "POST",
    body: JSON.stringify({
      name: file.name,
      size: file.size,
      contentType: file.type || "application/octet-stream",
      retention,
    }),
  });
}

export function completeUpload(uploadId: string): Promise<{ file: DiskFile }> {
  return request(`/api/uploads/${encodeURIComponent(uploadId)}/complete`, {
    method: "POST",
    body: "{}",
  });
}

export function cancelUpload(uploadId: string): Promise<void> {
  return request(`/api/uploads/${encodeURIComponent(uploadId)}`, {
    method: "DELETE",
    body: "{}",
  });
}

export function deleteFile(key: string): Promise<void> {
  return request("/api/files", { method: "DELETE", body: JSON.stringify({ key }) });
}

export async function getDownloadUrl(key: string): Promise<string> {
  const result = await request<{ url: string }>("/api/downloads", {
    method: "POST",
    body: JSON.stringify({ key }),
  });
  return result.url;
}

export function uploadToR2(
  ticket: UploadTicket,
  file: File,
  onProgress: (progress: number) => void,
): { promise: Promise<void>; abort: () => void } {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<void>((resolve, reject) => {
    xhr.open("PUT", ticket.url);
    for (const [name, value] of Object.entries(ticket.headers)) xhr.setRequestHeader(name, value);
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new ApiError(`文件上传失败（R2 ${xhr.status}）`, "R2_UPLOAD_FAILED", xhr.status));
    });
    xhr.addEventListener("error", () => reject(new ApiError("网络中断，文件上传失败", "NETWORK_ERROR")));
    xhr.addEventListener("abort", () => reject(new ApiError("上传已取消", "UPLOAD_ABORTED")));
    xhr.send(file);
  });
  return { promise, abort: () => xhr.abort() };
}
